import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { deriveSeatNumberAndUpdateStore, handleNewPackageData } from './apiDataHandlers';
import { updatePackageData } from './packageDataSlice';
import { resetPackageVars } from './sessionSlice';
import { performanceAddedEvent, performanceRemovedEvent } from '../utils';

export const tessituraApiSlice = createApi({
  reducerPath: 'tessituraApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/tessitura' }),
  endpoints: (builder) => ({
    addPerformance: builder.mutation<AddOrRemovePerformanceResponse, AddPerformanceArgs>({
      query: ({ packageLineNo, performanceId, zoneId }) => ({
        url: '/addPerformance',
        method: 'POST',
        body: {
          package_line_no: packageLineNo,
          performance_id: performanceId,
          zone_id: zoneId,
        },
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data) {
          handleNewPackageData(data, dispatch, true);

          // event below is used by CartTimerManager.js
          window.dispatchEvent(performanceAddedEvent);
        } else {
          throw Error('Error: no valid response data returned');
        }
      },
    }),
    addPerformanceSingleCyo: builder.mutation<AddOrRemovePerformanceResponse, AddPerformanceSingleCyoArgs>({
      query: ({ performanceId, zoneId }) => ({
        url: '/addPerformanceSingleCyo',
        method: 'POST',
        body: {
          performance_id: performanceId,
          zone_id: zoneId,
        },
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data) {
          handleNewPackageData(data, dispatch, true);

          // event below is used by CartTimerManager.js
          window.dispatchEvent(performanceAddedEvent);
        } else {
          throw Error('Error: no valid response data returned');
        }
      },
    }),
    cartOverview: builder.query<CartOverviewResponse, null>({
      query: () => ({
        url: '/cartOverview',
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data && !('message' in data)) {
          handleNewPackageData(data, dispatch, true);

          if (data.performancesInPackage.length > 0) {
            deriveSeatNumberAndUpdateStore(dispatch, data.performancesInPackage[0]);
          }
        } else if (data && data.message) {
          dispatch(resetPackageVars());
          dispatch(updatePackageData([]));
        }
      },
    }),
    concertPerformances: builder.query<ConcertPerformancesResponse, number>({
      query: (productionSeasonId) => ({
        url: `/concertPerformances?productionSeasonId=${productionSeasonId}`,
      }),
    }),
    removeAllPerformances: builder.mutation<AddOrRemovePerformanceResponse, number>({
      query: (packageLineNumber) => ({
        url: '/removePerformances',
        method: 'POST',
        body: {
          package_line_no: packageLineNumber,
        },
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data) {
          handleNewPackageData(data, dispatch, true);
        } else {
          throw Error('Error: no valid response data returned');
        }
      },
    }),
    removeAllPerformancesSingleCyo:
      builder.mutation<AddOrRemovePerformanceResponse, RemoveAllPerformanceSingleCyoArgs>(
        {
          query: (removeAllSinglePerformances) => ({
            url: '/removePerformancesSingleCyo',
            method: 'POST',
            body: {
              remove_all_single_performances: removeAllSinglePerformances,
            },
          }),
          async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
            await cacheDataLoaded;

            const { data } = getCacheEntry();

            if (data) {
              handleNewPackageData(data, dispatch, true);
            } else {
              throw Error('Error: no valid response data returned');
            }
          },
        },
      ),
    removePerformance: builder.mutation<AddOrRemovePerformanceResponse, RemovePerformanceArgs>({
      query: ({ packageLineNumber, performanceLineNumber, performanceId }) => ({
        url: '/removePerformances',
        method: 'POST',
        body: {
          package_line_no: packageLineNumber,
          performance_line_item_id: performanceLineNumber,
          performance_id: performanceId,
        },
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data) {
          handleNewPackageData(data, dispatch, true);

          // event below is used by CartTimerManager.js
          window.dispatchEvent(performanceRemovedEvent);
        } else {
          throw Error('Error: no valid response data returned');
        }
      },
    }),
    removePerformanceSingleCyo: builder.mutation<AddOrRemovePerformanceResponse, RemovePerformanceSingleCyoArgs>({
      query: ({ performanceLineNumber, performanceId }) => ({
        url: '/removePerformancesSingleCyo',
        method: 'POST',
        body: {
          performance_line_item_id: performanceLineNumber,
          performance_id: performanceId,
        },
      }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, getCacheEntry }) {
        await cacheDataLoaded;

        const { data } = getCacheEntry();

        if (data) {
          handleNewPackageData(data, dispatch, true);

          // event below is used by CartTimerManager.js
          window.dispatchEvent(performanceRemovedEvent);
        } else {
          throw Error('Error: no valid response data returned');
        }
      },
    }),
    selectSeats: builder.mutation<SelectSeatsResponse, SelectSeatsArgs>({
      query: ({ promoCode, seats, specialRequests }) => ({
        url: '/selectSeats',
        method: 'POST',
        body: {
          promoCode,
          seats,
          seats_special_request: specialRequests,
        },
      }),
    }),
  }),
});

export const {
  useAddPerformanceMutation,
  useAddPerformanceSingleCyoMutation,
  useConcertPerformancesQuery,
  useCartOverviewQuery,
  useRemoveAllPerformancesMutation,
  useRemoveAllPerformancesSingleCyoMutation,
  useRemovePerformanceMutation,
  useRemovePerformanceSingleCyoMutation,
  useSelectSeatsMutation,
} = tessituraApiSlice;
